/**
 * Options REST API
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    AccountTradeListResponse,
    CancelAllOptionOrdersByUnderlyingResponse,
    CancelAllOptionOrdersOnSpecificSymbolResponse,
    CancelMultipleOptionOrdersResponse,
    CancelOptionOrderResponse,
    NewOrderResponse,
    OptionPositionInformationResponse,
    PlaceMultipleOrdersOrdersParameterInner,
    PlaceMultipleOrdersResponse,
    QueryCurrentOpenOptionOrdersResponse,
    QueryOptionOrderHistoryResponse,
    QuerySingleOrderResponse,
    TradfiOptionsContractResponse,
    UserCommissionResponse,
    UserExerciseRecordResponse,
} from '../types';

/**
 * TradeApi - axios parameter creator
 */
const TradeApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Get trades for a specific account and symbol.
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * @summary Account Trade List (USER_DATA)
         * @param {string} symbol Option trading pair.
         * @param {number | bigint} [fromId] Trade id to fetch from. Default gets most recent trades, e.g 4611875134427365376
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        accountTradeList: async (
            symbol: string,
            fromId?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('accountTradeList', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (fromId !== undefined && fromId !== null) {
                localVarQueryParameter['fromId'] = fromId;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/userTrades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Cancel all active orders on specified underlying.
         *
         * Weight(IP): 5
         *
         * Security Type: TRADE
         *
         * @summary Cancel All Option Orders By Underlying (TRADE)
         * @param {string} underlying Underlying asset.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        cancelAllOptionOrdersByUnderlying: async (
            underlying: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('cancelAllOptionOrdersByUnderlying', 'underlying', underlying);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/allOpenOrdersByUnderlying',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Cancel all active order on a symbol.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * @summary Cancel all Option orders on specific symbol (TRADE)
         * @param {string} symbol Option trading pair.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        cancelAllOptionOrdersOnSpecificSymbol: async (
            symbol: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('cancelAllOptionOrdersOnSpecificSymbol', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/allOpenOrders',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Cancel multiple orders.
         *
         * Weight(IP): 5
         *
         * Security Type: TRADE
         *
         * Notes:
         * - At least one instance of `orderId` and `clientOrderId` must be sent.
         *
         * @summary Cancel Multiple Option Orders (TRADE)
         * @param {string} symbol Option trading pair.
         * @param {Array<number>} [orderIds] Order ID list.
         * @param {Array<string>} [clientOrderIds] Client order ID list.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        cancelMultipleOptionOrders: async (
            symbol: string,
            orderIds?: Array<number>,
            clientOrderIds?: Array<string>,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('cancelMultipleOptionOrders', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (orderIds) {
                localVarQueryParameter['orderIds'] = orderIds;
            }
            if (clientOrderIds) {
                localVarQueryParameter['clientOrderIds'] = clientOrderIds;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/batchOrders',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Cancel an active order.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * Notes:
         * - At least one instance of `orderId` and `clientOrderId` must be sent.
         *
         * @summary Cancel Option Order (TRADE)
         * @param {string} symbol Option trading pair.
         * @param {number | bigint} [orderId] Order ID.
         * @param {string} [clientOrderId] clientOrderId
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        cancelOptionOrder: async (
            symbol: string,
            orderId?: number | bigint,
            clientOrderId?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('cancelOptionOrder', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (orderId !== undefined && orderId !== null) {
                localVarQueryParameter['orderId'] = orderId;
            }
            if (clientOrderId !== undefined && clientOrderId !== null) {
                localVarQueryParameter['clientOrderId'] = clientOrderId;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/order',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Send a new order.
         *
         * Weight(IP): 0
         *
         * Security Type: TRADE
         *
         * Notes:
         * Some parameters are mandatory depending on the order type as follows:
         *
         * Type | Mandatory parameters
         * ------------ | ------------
         * LIMIT | timeInForce, quantity, price
         *
         * @summary New Order (TRADE)
         * @param {string} symbol
         * @param {NewOrderSideEnum} side
         * @param {NewOrderTypeEnum} type
         * @param {number} quantity Order Quantity
         * @param {number} [price] Order Price
         * @param {NewOrderTimeInForceEnum} [timeInForce]
         * @param {boolean} [reduceOnly]
         * @param {boolean} [postOnly]
         * @param {NewOrderNewOrderRespTypeEnum} [newOrderRespType]
         * @param {string} [clientOrderId] User-defined order ID cannot be repeated in pending orders
         * @param {boolean} [isMmp] is market maker protection order
         * @param {NewOrderSelfTradePreventionModeEnum} [selfTradePreventionMode] Self-trade prevention mode
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        newOrder: async (
            symbol: string,
            side: NewOrderSideEnum,
            type: NewOrderTypeEnum,
            quantity: number,
            price?: number,
            timeInForce?: NewOrderTimeInForceEnum,
            reduceOnly?: boolean,
            postOnly?: boolean,
            newOrderRespType?: NewOrderNewOrderRespTypeEnum,
            clientOrderId?: string,
            isMmp?: boolean,
            selfTradePreventionMode?: NewOrderSelfTradePreventionModeEnum,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('newOrder', 'symbol', symbol);
            // verify required parameter 'side' is not null or undefined
            assertParamExists('newOrder', 'side', side);
            // verify required parameter 'type' is not null or undefined
            assertParamExists('newOrder', 'type', type);
            // verify required parameter 'quantity' is not null or undefined
            assertParamExists('newOrder', 'quantity', quantity);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (side !== undefined && side !== null) {
                localVarQueryParameter['side'] = side;
            }
            if (type !== undefined && type !== null) {
                localVarQueryParameter['type'] = type;
            }
            if (quantity !== undefined && quantity !== null) {
                localVarQueryParameter['quantity'] = quantity;
            }
            if (price !== undefined && price !== null) {
                localVarQueryParameter['price'] = price;
            }
            if (timeInForce !== undefined && timeInForce !== null) {
                localVarQueryParameter['timeInForce'] = timeInForce;
            }
            if (reduceOnly !== undefined && reduceOnly !== null) {
                localVarQueryParameter['reduceOnly'] = reduceOnly;
            }
            if (postOnly !== undefined && postOnly !== null) {
                localVarQueryParameter['postOnly'] = postOnly;
            }
            if (newOrderRespType !== undefined && newOrderRespType !== null) {
                localVarQueryParameter['newOrderRespType'] = newOrderRespType;
            }
            if (clientOrderId !== undefined && clientOrderId !== null) {
                localVarQueryParameter['clientOrderId'] = clientOrderId;
            }
            if (isMmp !== undefined && isMmp !== null) {
                localVarQueryParameter['isMmp'] = isMmp;
            }
            if (selfTradePreventionMode !== undefined && selfTradePreventionMode !== null) {
                localVarQueryParameter['selfTradePreventionMode'] = selfTradePreventionMode;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/order',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get current position information.
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * @summary Option Position Information (USER_DATA)
         * @param {string} [symbol] Option trading pair.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        optionPositionInformation: async (
            symbol?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/position',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Send multiple option orders.
         *
         * Weight(IP): 5
         *
         * Security Type: TRADE
         *
         * Notes:
         * Some parameters are mandatory depending on the order type as follows:
         *
         * Type | Mandatory parameters
         * ------------ | ------------
         * LIMIT | timeInForce, quantity, price
         *
         * - Parameter rules are same with New Order
         * - Batch orders are processed concurrently, and the order of matching is not guaranteed.
         *
         * @summary Place Multiple Orders (TRADE)
         * @param {Array<PlaceMultipleOrdersOrdersParameterInner>} orders order list. Max 10 orders
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        placeMultipleOrders: async (
            orders: Array<PlaceMultipleOrdersOrdersParameterInner>,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'orders' is not null or undefined
            assertParamExists('placeMultipleOrders', 'orders', orders);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (orders) {
                localVarQueryParameter['orders'] = orders;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/batchOrders',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query current all open orders, status: ACCEPTED PARTIALLY_FILLED
         *
         * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
         *
         * Security Type: USER_DATA
         *
         * @summary Query Current Open Option Orders (USER_DATA)
         * @param {string} [symbol] Option trading pair.
         * @param {number | bigint} [orderId] Order ID.
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        queryCurrentOpenOptionOrders: async (
            symbol?: string,
            orderId?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (orderId !== undefined && orderId !== null) {
                localVarQueryParameter['orderId'] = orderId;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/openOrders',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query all finished orders within 5 days, finished status: CANCELLED FILLED REJECTED.
         *
         * Weight(IP): 3
         *
         * Security Type: TRADE
         *
         * @summary Query Option Order History (TRADE)
         * @param {string} symbol Option trading pair.
         * @param {number | bigint} [orderId] Order ID.
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        queryOptionOrderHistory: async (
            symbol: string,
            orderId?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('queryOptionOrderHistory', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (orderId !== undefined && orderId !== null) {
                localVarQueryParameter['orderId'] = orderId;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/historyOrders',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Check an order status.
         *
         * These orders will not be found:
         * order status is `CANCELED` or `REJECTED`, **AND**
         * order has NO filled trade, **AND**
         * created time + 3 days < current time
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * Notes:
         * - Either `orderId` or `clientOrderId ` must be sent.
         *
         * @summary Query Single Order (TRADE)
         * @param {string} symbol Option trading pair.
         * @param {number | bigint} [orderId] Order ID.
         * @param {string} [clientOrderId] User-defined order ID; cannot be duplicated among open orders.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        querySingleOrder: async (
            symbol: string,
            orderId?: number | bigint,
            clientOrderId?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('querySingleOrder', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (orderId !== undefined && orderId !== null) {
                localVarQueryParameter['orderId'] = orderId;
            }
            if (clientOrderId !== undefined && clientOrderId !== null) {
                localVarQueryParameter['clientOrderId'] = clientOrderId;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/order',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Sign TradFi Options agreement contract
         *
         * Weight(IP): 50
         *
         * Security Type: USER_DATA
         *
         * @summary TradFi Options Contract (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        tradfiOptionsContract: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/stock/contract',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get account commission.
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * @summary User Commission (USER_DATA)
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        userCommission: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/commission',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get account exercise records.
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * @summary User Exercise Record (USER_DATA)
         * @param {string} [symbol] Option trading pair.
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        userExerciseRecord: async (
            symbol?: string,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/exerciseRecord',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * TradeApi - interface
 * @interface TradeApi
 */
export interface TradeApiInterface {
    /**
     * Get trades for a specific account and symbol.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary Account Trade List (USER_DATA)
     * @param {AccountTradeListRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    accountTradeList(
        requestParameters: AccountTradeListRequest
    ): Promise<RestApiResponse<AccountTradeListResponse>>;
    /**
     * Cancel all active orders on specified underlying.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * @summary Cancel All Option Orders By Underlying (TRADE)
     * @param {CancelAllOptionOrdersByUnderlyingRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    cancelAllOptionOrdersByUnderlying(
        requestParameters: CancelAllOptionOrdersByUnderlyingRequest
    ): Promise<RestApiResponse<CancelAllOptionOrdersByUnderlyingResponse>>;
    /**
     * Cancel all active order on a symbol.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Cancel all Option orders on specific symbol (TRADE)
     * @param {CancelAllOptionOrdersOnSpecificSymbolRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    cancelAllOptionOrdersOnSpecificSymbol(
        requestParameters: CancelAllOptionOrdersOnSpecificSymbolRequest
    ): Promise<RestApiResponse<CancelAllOptionOrdersOnSpecificSymbolResponse>>;
    /**
     * Cancel multiple orders.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * - At least one instance of `orderId` and `clientOrderId` must be sent.
     *
     * @summary Cancel Multiple Option Orders (TRADE)
     * @param {CancelMultipleOptionOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    cancelMultipleOptionOrders(
        requestParameters: CancelMultipleOptionOrdersRequest
    ): Promise<RestApiResponse<CancelMultipleOptionOrdersResponse>>;
    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - At least one instance of `orderId` and `clientOrderId` must be sent.
     *
     * @summary Cancel Option Order (TRADE)
     * @param {CancelOptionOrderRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    cancelOptionOrder(
        requestParameters: CancelOptionOrderRequest
    ): Promise<RestApiResponse<CancelOptionOrderResponse>>;
    /**
     * Send a new order.
     *
     * Weight(IP): 0
     *
     * Security Type: TRADE
     *
     * Notes:
     * Some parameters are mandatory depending on the order type as follows:
     *
     * Type | Mandatory parameters
     * ------------ | ------------
     * LIMIT | timeInForce, quantity, price
     *
     * @summary New Order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    newOrder(requestParameters: NewOrderRequest): Promise<RestApiResponse<NewOrderResponse>>;
    /**
     * Get current position information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary Option Position Information (USER_DATA)
     * @param {OptionPositionInformationRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    optionPositionInformation(
        requestParameters?: OptionPositionInformationRequest
    ): Promise<RestApiResponse<OptionPositionInformationResponse>>;
    /**
     * Send multiple option orders.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * Some parameters are mandatory depending on the order type as follows:
     *
     * Type | Mandatory parameters
     * ------------ | ------------
     * LIMIT | timeInForce, quantity, price
     *
     * - Parameter rules are same with New Order
     * - Batch orders are processed concurrently, and the order of matching is not guaranteed.
     *
     * @summary Place Multiple Orders (TRADE)
     * @param {PlaceMultipleOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    placeMultipleOrders(
        requestParameters: PlaceMultipleOrdersRequest
    ): Promise<RestApiResponse<PlaceMultipleOrdersResponse>>;
    /**
     * Query current all open orders, status: ACCEPTED PARTIALLY_FILLED
     *
     * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
     *
     * Security Type: USER_DATA
     *
     * @summary Query Current Open Option Orders (USER_DATA)
     * @param {QueryCurrentOpenOptionOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    queryCurrentOpenOptionOrders(
        requestParameters?: QueryCurrentOpenOptionOrdersRequest
    ): Promise<RestApiResponse<QueryCurrentOpenOptionOrdersResponse>>;
    /**
     * Query all finished orders within 5 days, finished status: CANCELLED FILLED REJECTED.
     *
     * Weight(IP): 3
     *
     * Security Type: TRADE
     *
     * @summary Query Option Order History (TRADE)
     * @param {QueryOptionOrderHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    queryOptionOrderHistory(
        requestParameters: QueryOptionOrderHistoryRequest
    ): Promise<RestApiResponse<QueryOptionOrderHistoryResponse>>;
    /**
     * Check an order status.
     *
     * These orders will not be found:
     * order status is `CANCELED` or `REJECTED`, **AND**
     * order has NO filled trade, **AND**
     * created time + 3 days < current time
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `clientOrderId ` must be sent.
     *
     * @summary Query Single Order (TRADE)
     * @param {QuerySingleOrderRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    querySingleOrder(
        requestParameters: QuerySingleOrderRequest
    ): Promise<RestApiResponse<QuerySingleOrderResponse>>;
    /**
     * Sign TradFi Options agreement contract
     *
     * Weight(IP): 50
     *
     * Security Type: USER_DATA
     *
     * @summary TradFi Options Contract (USER_DATA)
     * @param {TradfiOptionsContractRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    tradfiOptionsContract(
        requestParameters?: TradfiOptionsContractRequest
    ): Promise<RestApiResponse<TradfiOptionsContractResponse>>;
    /**
     * Get account commission.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary User Commission (USER_DATA)
     * @param {UserCommissionRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    userCommission(
        requestParameters?: UserCommissionRequest
    ): Promise<RestApiResponse<UserCommissionResponse>>;
    /**
     * Get account exercise records.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary User Exercise Record (USER_DATA)
     * @param {UserExerciseRecordRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApiInterface
     */
    userExerciseRecord(
        requestParameters?: UserExerciseRecordRequest
    ): Promise<RestApiResponse<UserExerciseRecordResponse>>;
}

/**
 * Request parameters for accountTradeList operation in TradeApi.
 * @interface AccountTradeListRequest
 */
export interface AccountTradeListRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiAccountTradeList
     */
    readonly symbol: string;

    /**
     * Trade id to fetch from. Default gets most recent trades, e.g 4611875134427365376
     * @type {number | bigint}
     * @memberof TradeApiAccountTradeList
     */
    readonly fromId?: number | bigint;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof TradeApiAccountTradeList
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof TradeApiAccountTradeList
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned.
     * @type {number | bigint}
     * @memberof TradeApiAccountTradeList
     */
    readonly limit?: number | bigint;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiAccountTradeList
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for cancelAllOptionOrdersByUnderlying operation in TradeApi.
 * @interface CancelAllOptionOrdersByUnderlyingRequest
 */
export interface CancelAllOptionOrdersByUnderlyingRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof TradeApiCancelAllOptionOrdersByUnderlying
     */
    readonly underlying: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiCancelAllOptionOrdersByUnderlying
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for cancelAllOptionOrdersOnSpecificSymbol operation in TradeApi.
 * @interface CancelAllOptionOrdersOnSpecificSymbolRequest
 */
export interface CancelAllOptionOrdersOnSpecificSymbolRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiCancelAllOptionOrdersOnSpecificSymbol
     */
    readonly symbol: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiCancelAllOptionOrdersOnSpecificSymbol
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for cancelMultipleOptionOrders operation in TradeApi.
 * @interface CancelMultipleOptionOrdersRequest
 */
export interface CancelMultipleOptionOrdersRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiCancelMultipleOptionOrders
     */
    readonly symbol: string;

    /**
     * Order ID list.
     * @type {Array<number>}
     * @memberof TradeApiCancelMultipleOptionOrders
     */
    readonly orderIds?: Array<number>;

    /**
     * Client order ID list.
     * @type {Array<string>}
     * @memberof TradeApiCancelMultipleOptionOrders
     */
    readonly clientOrderIds?: Array<string>;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiCancelMultipleOptionOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for cancelOptionOrder operation in TradeApi.
 * @interface CancelOptionOrderRequest
 */
export interface CancelOptionOrderRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiCancelOptionOrder
     */
    readonly symbol: string;

    /**
     * Order ID.
     * @type {number | bigint}
     * @memberof TradeApiCancelOptionOrder
     */
    readonly orderId?: number | bigint;

    /**
     * clientOrderId
     * @type {string}
     * @memberof TradeApiCancelOptionOrder
     */
    readonly clientOrderId?: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiCancelOptionOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for newOrder operation in TradeApi.
 * @interface NewOrderRequest
 */
export interface NewOrderRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiNewOrder
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiNewOrder
     */
    readonly side: NewOrderSideEnum;

    /**
     *
     * @type {'LIMIT'}
     * @memberof TradeApiNewOrder
     */
    readonly type: NewOrderTypeEnum;

    /**
     * Order Quantity
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly quantity: number;

    /**
     * Order Price
     * @type {number}
     * @memberof TradeApiNewOrder
     */
    readonly price?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'GTX'}
     * @memberof TradeApiNewOrder
     */
    readonly timeInForce?: NewOrderTimeInForceEnum;

    /**
     *
     * @type {boolean}
     * @memberof TradeApiNewOrder
     */
    readonly reduceOnly?: boolean;

    /**
     *
     * @type {boolean}
     * @memberof TradeApiNewOrder
     */
    readonly postOnly?: boolean;

    /**
     *
     * @type {'ACK' | 'RESULT'}
     * @memberof TradeApiNewOrder
     */
    readonly newOrderRespType?: NewOrderNewOrderRespTypeEnum;

    /**
     * User-defined order ID cannot be repeated in pending orders
     * @type {string}
     * @memberof TradeApiNewOrder
     */
    readonly clientOrderId?: string;

    /**
     * is market maker protection order
     * @type {boolean}
     * @memberof TradeApiNewOrder
     */
    readonly isMmp?: boolean;

    /**
     * Self-trade prevention mode
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH'}
     * @memberof TradeApiNewOrder
     */
    readonly selfTradePreventionMode?: NewOrderSelfTradePreventionModeEnum;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiNewOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for optionPositionInformation operation in TradeApi.
 * @interface OptionPositionInformationRequest
 */
export interface OptionPositionInformationRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiOptionPositionInformation
     */
    readonly symbol?: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiOptionPositionInformation
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for placeMultipleOrders operation in TradeApi.
 * @interface PlaceMultipleOrdersRequest
 */
export interface PlaceMultipleOrdersRequest {
    /**
     * order list. Max 10 orders
     * @type {Array<PlaceMultipleOrdersOrdersParameterInner>}
     * @memberof TradeApiPlaceMultipleOrders
     */
    readonly orders: Array<PlaceMultipleOrdersOrdersParameterInner>;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiPlaceMultipleOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryCurrentOpenOptionOrders operation in TradeApi.
 * @interface QueryCurrentOpenOptionOrdersRequest
 */
export interface QueryCurrentOpenOptionOrdersRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiQueryCurrentOpenOptionOrders
     */
    readonly symbol?: string;

    /**
     * Order ID.
     * @type {number | bigint}
     * @memberof TradeApiQueryCurrentOpenOptionOrders
     */
    readonly orderId?: number | bigint;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof TradeApiQueryCurrentOpenOptionOrders
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof TradeApiQueryCurrentOpenOptionOrders
     */
    readonly endTime?: number | bigint;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiQueryCurrentOpenOptionOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryOptionOrderHistory operation in TradeApi.
 * @interface QueryOptionOrderHistoryRequest
 */
export interface QueryOptionOrderHistoryRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly symbol: string;

    /**
     * Order ID.
     * @type {number | bigint}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly orderId?: number | bigint;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned
     * @type {number | bigint}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly limit?: number | bigint;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiQueryOptionOrderHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for querySingleOrder operation in TradeApi.
 * @interface QuerySingleOrderRequest
 */
export interface QuerySingleOrderRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiQuerySingleOrder
     */
    readonly symbol: string;

    /**
     * Order ID.
     * @type {number | bigint}
     * @memberof TradeApiQuerySingleOrder
     */
    readonly orderId?: number | bigint;

    /**
     * User-defined order ID; cannot be duplicated among open orders.
     * @type {string}
     * @memberof TradeApiQuerySingleOrder
     */
    readonly clientOrderId?: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiQuerySingleOrder
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for tradfiOptionsContract operation in TradeApi.
 * @interface TradfiOptionsContractRequest
 */
export interface TradfiOptionsContractRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiTradfiOptionsContract
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for userCommission operation in TradeApi.
 * @interface UserCommissionRequest
 */
export interface UserCommissionRequest {
    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiUserCommission
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for userExerciseRecord operation in TradeApi.
 * @interface UserExerciseRecordRequest
 */
export interface UserExerciseRecordRequest {
    /**
     * Option trading pair.
     * @type {string}
     * @memberof TradeApiUserExerciseRecord
     */
    readonly symbol?: string;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof TradeApiUserExerciseRecord
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof TradeApiUserExerciseRecord
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned.
     * @type {number | bigint}
     * @memberof TradeApiUserExerciseRecord
     */
    readonly limit?: number | bigint;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof TradeApiUserExerciseRecord
     */
    readonly recvWindow?: number | bigint;
}

/**
 * TradeApi - object-oriented interface
 * @class TradeApi
 */
export class TradeApi implements TradeApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = TradeApiAxiosParamCreator(configuration);
    }

    /**
     * Get trades for a specific account and symbol.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary Account Trade List (USER_DATA)
     * @param {AccountTradeListRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<AccountTradeListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#account-trade-list Binance API Documentation}
     */
    public async accountTradeList(
        requestParameters: AccountTradeListRequest
    ): Promise<RestApiResponse<AccountTradeListResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.accountTradeList(
            requestParameters?.symbol,
            requestParameters?.fromId,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit,
            requestParameters?.recvWindow
        );
        return sendRequest<AccountTradeListResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Cancel all active orders on specified underlying.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * @summary Cancel All Option Orders By Underlying (TRADE)
     * @param {CancelAllOptionOrdersByUnderlyingRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<CancelAllOptionOrdersByUnderlyingResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#cancel-all-option-orders-by-underlying Binance API Documentation}
     */
    public async cancelAllOptionOrdersByUnderlying(
        requestParameters: CancelAllOptionOrdersByUnderlyingRequest
    ): Promise<RestApiResponse<CancelAllOptionOrdersByUnderlyingResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.cancelAllOptionOrdersByUnderlying(
                requestParameters?.underlying,
                requestParameters?.recvWindow
            );
        return sendRequest<CancelAllOptionOrdersByUnderlyingResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Cancel all active order on a symbol.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Cancel all Option orders on specific symbol (TRADE)
     * @param {CancelAllOptionOrdersOnSpecificSymbolRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<CancelAllOptionOrdersOnSpecificSymbolResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#cancel-all-option-orders-on-specific-symbol Binance API Documentation}
     */
    public async cancelAllOptionOrdersOnSpecificSymbol(
        requestParameters: CancelAllOptionOrdersOnSpecificSymbolRequest
    ): Promise<RestApiResponse<CancelAllOptionOrdersOnSpecificSymbolResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.cancelAllOptionOrdersOnSpecificSymbol(
                requestParameters?.symbol,
                requestParameters?.recvWindow
            );
        return sendRequest<CancelAllOptionOrdersOnSpecificSymbolResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Cancel multiple orders.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * - At least one instance of `orderId` and `clientOrderId` must be sent.
     *
     * @summary Cancel Multiple Option Orders (TRADE)
     * @param {CancelMultipleOptionOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<CancelMultipleOptionOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#cancel-multiple-option-orders Binance API Documentation}
     */
    public async cancelMultipleOptionOrders(
        requestParameters: CancelMultipleOptionOrdersRequest
    ): Promise<RestApiResponse<CancelMultipleOptionOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.cancelMultipleOptionOrders(
            requestParameters?.symbol,
            requestParameters?.orderIds,
            requestParameters?.clientOrderIds,
            requestParameters?.recvWindow
        );
        return sendRequest<CancelMultipleOptionOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - At least one instance of `orderId` and `clientOrderId` must be sent.
     *
     * @summary Cancel Option Order (TRADE)
     * @param {CancelOptionOrderRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<CancelOptionOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#cancel-option-order Binance API Documentation}
     */
    public async cancelOptionOrder(
        requestParameters: CancelOptionOrderRequest
    ): Promise<RestApiResponse<CancelOptionOrderResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.cancelOptionOrder(
            requestParameters?.symbol,
            requestParameters?.orderId,
            requestParameters?.clientOrderId,
            requestParameters?.recvWindow
        );
        return sendRequest<CancelOptionOrderResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Send a new order.
     *
     * Weight(IP): 0
     *
     * Security Type: TRADE
     *
     * Notes:
     * Some parameters are mandatory depending on the order type as follows:
     *
     * Type | Mandatory parameters
     * ------------ | ------------
     * LIMIT | timeInForce, quantity, price
     *
     * @summary New Order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<NewOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#new-order Binance API Documentation}
     */
    public async newOrder(
        requestParameters: NewOrderRequest
    ): Promise<RestApiResponse<NewOrderResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.newOrder(
            requestParameters?.symbol,
            requestParameters?.side,
            requestParameters?.type,
            requestParameters?.quantity,
            requestParameters?.price,
            requestParameters?.timeInForce,
            requestParameters?.reduceOnly,
            requestParameters?.postOnly,
            requestParameters?.newOrderRespType,
            requestParameters?.clientOrderId,
            requestParameters?.isMmp,
            requestParameters?.selfTradePreventionMode,
            requestParameters?.recvWindow
        );
        return sendRequest<NewOrderResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get current position information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary Option Position Information (USER_DATA)
     * @param {OptionPositionInformationRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OptionPositionInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#option-position-information Binance API Documentation}
     */
    public async optionPositionInformation(
        requestParameters: OptionPositionInformationRequest = {}
    ): Promise<RestApiResponse<OptionPositionInformationResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.optionPositionInformation(
            requestParameters?.symbol,
            requestParameters?.recvWindow
        );
        return sendRequest<OptionPositionInformationResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Send multiple option orders.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * Some parameters are mandatory depending on the order type as follows:
     *
     * Type | Mandatory parameters
     * ------------ | ------------
     * LIMIT | timeInForce, quantity, price
     *
     * - Parameter rules are same with New Order
     * - Batch orders are processed concurrently, and the order of matching is not guaranteed.
     *
     * @summary Place Multiple Orders (TRADE)
     * @param {PlaceMultipleOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<PlaceMultipleOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#place-multiple-orders Binance API Documentation}
     */
    public async placeMultipleOrders(
        requestParameters: PlaceMultipleOrdersRequest
    ): Promise<RestApiResponse<PlaceMultipleOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.placeMultipleOrders(
            requestParameters?.orders,
            requestParameters?.recvWindow
        );
        return sendRequest<PlaceMultipleOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query current all open orders, status: ACCEPTED PARTIALLY_FILLED
     *
     * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
     *
     * Security Type: USER_DATA
     *
     * @summary Query Current Open Option Orders (USER_DATA)
     * @param {QueryCurrentOpenOptionOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryCurrentOpenOptionOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#query-current-open-option-orders Binance API Documentation}
     */
    public async queryCurrentOpenOptionOrders(
        requestParameters: QueryCurrentOpenOptionOrdersRequest = {}
    ): Promise<RestApiResponse<QueryCurrentOpenOptionOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.queryCurrentOpenOptionOrders(
            requestParameters?.symbol,
            requestParameters?.orderId,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.recvWindow
        );
        return sendRequest<QueryCurrentOpenOptionOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query all finished orders within 5 days, finished status: CANCELLED FILLED REJECTED.
     *
     * Weight(IP): 3
     *
     * Security Type: TRADE
     *
     * @summary Query Option Order History (TRADE)
     * @param {QueryOptionOrderHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryOptionOrderHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#query-option-order-history Binance API Documentation}
     */
    public async queryOptionOrderHistory(
        requestParameters: QueryOptionOrderHistoryRequest
    ): Promise<RestApiResponse<QueryOptionOrderHistoryResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.queryOptionOrderHistory(
            requestParameters?.symbol,
            requestParameters?.orderId,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit,
            requestParameters?.recvWindow
        );
        return sendRequest<QueryOptionOrderHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Check an order status.
     *
     * These orders will not be found:
     * order status is `CANCELED` or `REJECTED`, **AND**
     * order has NO filled trade, **AND**
     * created time + 3 days < current time
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `clientOrderId ` must be sent.
     *
     * @summary Query Single Order (TRADE)
     * @param {QuerySingleOrderRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QuerySingleOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#query-single-order Binance API Documentation}
     */
    public async querySingleOrder(
        requestParameters: QuerySingleOrderRequest
    ): Promise<RestApiResponse<QuerySingleOrderResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.querySingleOrder(
            requestParameters?.symbol,
            requestParameters?.orderId,
            requestParameters?.clientOrderId,
            requestParameters?.recvWindow
        );
        return sendRequest<QuerySingleOrderResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Sign TradFi Options agreement contract
     *
     * Weight(IP): 50
     *
     * Security Type: USER_DATA
     *
     * @summary TradFi Options Contract (USER_DATA)
     * @param {TradfiOptionsContractRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<TradfiOptionsContractResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#tradfi-options-contract Binance API Documentation}
     */
    public async tradfiOptionsContract(
        requestParameters: TradfiOptionsContractRequest = {}
    ): Promise<RestApiResponse<TradfiOptionsContractResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.tradfiOptionsContract(
            requestParameters?.recvWindow
        );
        return sendRequest<TradfiOptionsContractResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get account commission.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary User Commission (USER_DATA)
     * @param {UserCommissionRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<UserCommissionResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#user-commission Binance API Documentation}
     */
    public async userCommission(
        requestParameters: UserCommissionRequest = {}
    ): Promise<RestApiResponse<UserCommissionResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.userCommission(
            requestParameters?.recvWindow
        );
        return sendRequest<UserCommissionResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get account exercise records.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary User Exercise Record (USER_DATA)
     * @param {UserExerciseRecordRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<UserExerciseRecordResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/trade#user-exercise-record Binance API Documentation}
     */
    public async userExerciseRecord(
        requestParameters: UserExerciseRecordRequest = {}
    ): Promise<RestApiResponse<UserExerciseRecordResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.userExerciseRecord(
            requestParameters?.symbol,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit,
            requestParameters?.recvWindow
        );
        return sendRequest<UserExerciseRecordResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}

export enum NewOrderSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum NewOrderTypeEnum {
    LIMIT = 'LIMIT',
}

export enum NewOrderTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    GTX = 'GTX',
}

export enum NewOrderNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
}

export enum NewOrderSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
}
